import type { DataTableMode } from '@odo/shared-ui';
import type {
    DmInboxStatus,
    DmNotificationSort,
    DmNotificationSortOrder
} from '../../../services/dmNotifications';
import { decodeUrlArray, encodeUrlArray } from '../../../hooks/useUrlArrayParam';
import {
    DEFAULT_INBOX_STATUS,
    parseInboxStatus,
    sortForStatus,
    type ClosedSortRule
} from '../shared/inboxStatus';
import { parseInboxSearch, pickListSearch } from '../shared/listRouting';
import type { ListViewRoutes } from '../shared/useListViewNavigation';
import { DEFAULT_FOLD_MODE } from './inboxFolds';

export const NOTIFICATIONS_LIST_PATH = '/monitoring/notifications';

export const LIST_PARAM = {
    search: 'search',
    levels: 'levels',
    practiceIds: 'practiceIds',
    assignees: 'assignees',
    tags: 'tags',
    status: 'status',
    rows: 'rows',
    sort: 'sort',
    order: 'order',
    mode: 'mode'
} as const;

const LIST_PARAM_KEYS: readonly string[] = Object.values(LIST_PARAM);

export const DEFAULT_SORT: DmNotificationSort = 'level';
export const DEFAULT_ORDER: DmNotificationSortOrder = 'desc';
export const DEFAULT_MODE: DataTableMode = 'infinite';

// The list's filters as they sit in the URL; levels, assignees and tags are UI values, not API ones.
export interface NotificationListParams {
    search: string;
    levels: string[];
    practiceIds: string[];
    assignees: string[];
    tags: string[];
    status: DmInboxStatus;
    sort: DmNotificationSort;
    order: DmNotificationSortOrder;
    mode: DataTableMode;
}

const NOTIFICATION_SORTS: readonly string[] = [
    'level',
    'latest',
    'waiting'
] satisfies DmNotificationSort[];

function isNotificationSort(raw: string | null): raw is DmNotificationSort {
    return raw !== null && NOTIFICATION_SORTS.includes(raw);
}

export const CLOSED_SORT_RULE: ClosedSortRule<DmNotificationSort> = {
    openOnly: ['waiting'],
    fallback: 'latest'
};

export function parseSort(raw: string | null, status: DmInboxStatus): DmNotificationSort {
    return sortForStatus(isNotificationSort(raw) ? raw : DEFAULT_SORT, status, CLOSED_SORT_RULE);
}

export function parseOrder(raw: string | null): DmNotificationSortOrder {
    return raw === 'asc' || raw === 'desc' ? raw : DEFAULT_ORDER;
}

export function parseMode(raw: string | null): DataTableMode {
    return raw === 'paged' || raw === 'infinite' ? raw : DEFAULT_MODE;
}

export function readListParams(searchParams: URLSearchParams): NotificationListParams {
    const list = (key: string) => decodeUrlArray(searchParams.get(key) ?? '');
    const status = parseInboxStatus(searchParams.get(LIST_PARAM.status));
    return {
        search: parseInboxSearch(searchParams.get(LIST_PARAM.search)),
        levels: list(LIST_PARAM.levels),
        practiceIds: list(LIST_PARAM.practiceIds),
        assignees: list(LIST_PARAM.assignees),
        tags: list(LIST_PARAM.tags),
        status,
        sort: parseSort(searchParams.get(LIST_PARAM.sort), status),
        order: parseOrder(searchParams.get(LIST_PARAM.order)),
        mode: parseMode(searchParams.get(LIST_PARAM.mode))
    };
}

// Defaults are written as "absent" so a fresh list keeps a bare URL.
export function listParamValue(
    key: (typeof LIST_PARAM)[keyof typeof LIST_PARAM],
    value: string | string[]
): string | null {
    const encoded = Array.isArray(value) ? encodeUrlArray(value) : value.trim();
    const defaults: Record<string, string> = {
        [LIST_PARAM.sort]: DEFAULT_SORT,
        [LIST_PARAM.order]: DEFAULT_ORDER,
        [LIST_PARAM.mode]: DEFAULT_MODE,
        [LIST_PARAM.status]: DEFAULT_INBOX_STATUS,
        [LIST_PARAM.rows]: DEFAULT_FOLD_MODE
    };
    return encoded === '' || encoded === defaults[key] ? null : encoded;
}

export function pickNotificationListSearch(searchParams: URLSearchParams): string {
    return pickListSearch(searchParams, LIST_PARAM_KEYS);
}

export function notificationViewPath(patientId: string, listSearch: string): string {
    return `${NOTIFICATIONS_LIST_PATH}/${encodeURIComponent(patientId)}${listSearch}`;
}

export function notificationListPath(listSearch: string): string {
    return `${NOTIFICATIONS_LIST_PATH}${listSearch}`;
}

const FROM_LIST_FLAG = 'fromNotificationList';

// Router state the list sets on View, so the view's Back can pop history instead of pushing.
export interface FromNotificationListState {
    fromNotificationList: true;
}

export const NOTIFICATION_VIEW_ROUTES: ListViewRoutes = {
    listKeys: LIST_PARAM_KEYS,
    fromListFlag: FROM_LIST_FLAG,
    listPath: notificationListPath,
    viewPath: notificationViewPath
};
